import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './GridFlyout.css';

const GridFlyout = ({
  onClose,
  triggerRef,
  anchorRect,
  showGrid,
  snapToGrid,
  gridOpacity,
  gridSize,
  gridWidth,
  gridHeight,
  isGameMaster,
  onGridToggle,
  onSnapToggle,
  onGridOpacityChange,
  onGridSizeChange,
  onGridWidthChange,
  onGridHeightChange,
}) => {
  const panelRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const clickedInsidePanel = panelRef.current?.contains(event.target);
      const clickedTrigger = triggerRef?.current?.contains?.(event.target);

      if (!clickedInsidePanel && !clickedTrigger) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [onClose, triggerRef]);

  const panelStyle = useMemo(() => {
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
    const left = Math.max(12, Math.min(anchorRect?.left ?? 12, viewportWidth - 360));
    const top = Math.max((anchorRect?.bottom ?? 0) + 6, 12);

    return {
      position: 'fixed',
      top,
      left
    };
  }, [anchorRect]);

  const panel = (
    <div
      ref={panelRef}
      className="grid-flyout-panel"
      role="dialog"
      aria-label="Grid tools"
      style={panelStyle}
      data-testid="battle-toolbar-grid-flyout"
    >
      <div className="grid-flyout-panel-header">
        <div className="grid-flyout-panel-title">
          <span>Grid Settings</span>
        </div>
        <button
          type="button"
          className="grid-flyout-panel-close"
          onClick={onClose}
          aria-label="Close grid settings"
        >
          ×
        </button>
      </div>

      <div className="grid-flyout-panel-body">
        <div className="grid-flyout-row">
          <span className="grid-flyout-label">Grid</span>
          <button
            type="button"
            className={`grid-flyout-toggle ${showGrid ? 'is-active' : ''}`}
            aria-pressed={showGrid}
            onClick={onGridToggle}
          >
            {showGrid ? 'On' : 'Off'}
          </button>
        </div>

        <div className="grid-flyout-row">
          <span className="grid-flyout-label">Snap</span>
          <button
            type="button"
            className={`grid-flyout-toggle ${snapToGrid ? 'is-active' : ''}`}
            aria-pressed={snapToGrid}
            onClick={onSnapToggle}
          >
            {snapToGrid ? 'On' : 'Off'}
          </button>
        </div>

        <div className="grid-flyout-row grid-flyout-row-slider">
          <label htmlFor="gridOpacityCompact" className="grid-flyout-label">Opacity</label>
          <div className="grid-flyout-slider-wrap">
            <input
              id="gridOpacityCompact"
              type="range"
              min="0.0"
              max="1.0"
              step="0.05"
              value={gridOpacity}
              onChange={(event) => onGridOpacityChange(parseFloat(event.target.value))}
              className="grid-flyout-slider"
            />
            <span className="grid-flyout-slider-value">{Math.round(gridOpacity * 100)}%</span>
          </div>
        </div>

        {isGameMaster && (
          <>
            <div className="grid-flyout-row grid-flyout-row-slider">
              <label htmlFor="gridSizeCompact" className="grid-flyout-label">Size</label>
              <div className="grid-flyout-slider-wrap">
                <input
                  id="gridSizeCompact"
                  type="range"
                  min="20"
                  max="100"
                  step="5"
                  value={gridSize}
                  onChange={(event) => onGridSizeChange(parseInt(event.target.value, 10))}
                  className="grid-flyout-slider"
                />
                <span className="grid-flyout-slider-value">{gridSize}px</span>
              </div>
            </div>

            <div className="grid-flyout-section">
              <span className="grid-flyout-section-label">Map Extents</span>
              <div className="grid-flyout-dimension-grid">
                <label className="grid-flyout-field">
                  <span className="grid-flyout-field-label">Cols</span>
                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={gridWidth}
                    onChange={(event) => onGridWidthChange(event.target.value)}
                    onBlur={(event) => {
                      if (event.target.value === '' || parseInt(event.target.value, 10) < 10) {
                        onGridWidthChange(40);
                      }
                    }}
                    className="grid-flyout-number-input"
                    title="Grid width in cells (10-200)"
                  />
                </label>

                <label className="grid-flyout-field">
                  <span className="grid-flyout-field-label">Rows</span>
                  <input
                    type="number"
                    min="10"
                    max="200"
                    value={gridHeight}
                    onChange={(event) => onGridHeightChange(event.target.value)}
                    onBlur={(event) => {
                      if (event.target.value === '' || parseInt(event.target.value, 10) < 10) {
                        onGridHeightChange(30);
                      }
                    }}
                    className="grid-flyout-number-input"
                    title="Grid height in cells (10-200)"
                  />
                </label>
              </div>
            </div>
          </>
        )}

        <p className="grid-flyout-hint">
          Keep the tabletop clean during play and open this panel only when you need to tune the map grid.
        </p>
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return panel;
  }

  return createPortal(panel, document.body);
};

GridFlyout.propTypes = {
  onClose: PropTypes.func.isRequired,
  triggerRef: PropTypes.shape({ current: PropTypes.any }),
  anchorRect: PropTypes.shape({
    top: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number,
    right: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  showGrid: PropTypes.bool.isRequired,
  snapToGrid: PropTypes.bool.isRequired,
  gridOpacity: PropTypes.number,
  gridSize: PropTypes.number,
  gridWidth: PropTypes.number,
  gridHeight: PropTypes.number,
  isGameMaster: PropTypes.bool,
  onGridToggle: PropTypes.func.isRequired,
  onSnapToggle: PropTypes.func.isRequired,
  onGridOpacityChange: PropTypes.func.isRequired,
  onGridSizeChange: PropTypes.func.isRequired,
  onGridWidthChange: PropTypes.func.isRequired,
  onGridHeightChange: PropTypes.func.isRequired,
};

export default GridFlyout;
